import { useEffect, useState } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';

import { marketingMenus } from '@playbooks/components/mkt/mkt-menu-config';
import { MktMenuPanel } from '@playbooks/components/mkt/mkt-menu-panel';
import { useClient } from '@playbooks/contexts';
import { NavigationMenu } from '@playbooks/ui/components/navigation-menu';
import { cn } from '@playbooks/ui/lib/utils';

const triggerClassName =
	'flex-start h-8 rounded-panel px-3 text-sm font-normal text-foreground-nav outline-none transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-focus/50 data-[popup-open]:bg-surface-hover data-[popup-open]:text-foreground';
const contentClassName =
	'transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none data-starting-style:opacity-0 data-ending-style:opacity-0 data-starting-style:data-[activation-direction=left]:-translate-x-4 data-starting-style:data-[activation-direction=right]:translate-x-4 data-ending-style:data-[activation-direction=left]:translate-x-4 data-ending-style:data-[activation-direction=right]:-translate-x-4';

const MktMenu = () => {
	const [value, setValue] = useState<string | null>(null);
	const router = useRouter();
	const { platformHref } = useClient();

	// Hooks
	useEffect(() => {
		const onRouteChange = () => setValue(null);
		router.events.on('routeChangeStart', onRouteChange);
		return () => router.events.off('routeChangeStart', onRouteChange);
	}, [router.events]);

	// Render
	return (
		<NavigationMenu.Root value={value} onValueChange={setValue} className='hidden lg:block'>
			<NavigationMenu.List className='flex-start gap-1'>
				{marketingMenus.map(menu => (
					<NavigationMenu.Item key={menu.value} value={menu.value}>
						<NavigationMenu.Trigger className={triggerClassName}>{menu.label}</NavigationMenu.Trigger>
						<NavigationMenu.Content
							className={cn(
								contentClassName,
								menu.groups.length === 2 ? 'w-[min(40rem,calc(100vw-2rem))]' : 'w-[min(56rem,calc(100vw-2rem))]',
							)}>
							<MktMenuPanel menu={menu} />
						</NavigationMenu.Content>
					</NavigationMenu.Item>
				))}
				<NavigationMenu.Item>
					<NavigationMenu.Link render={<NextLink href={platformHref('/pricing')} />} className={triggerClassName}>
						Pricing
					</NavigationMenu.Link>
				</NavigationMenu.Item>
			</NavigationMenu.List>

			<NavigationMenu.Portal>
				<NavigationMenu.Positioner
					className='z-flyout'
					sideOffset={8}
					collisionPadding={{ top: 8, bottom: 8, left: 16, right: 16 }}
					collisionAvoidance={{ side: 'none' }}>
					<NavigationMenu.Popup className='h-(--popup-height) w-(--popup-width) origin-(--transform-origin) overflow-hidden rounded-panel border bg-surface-raised text-foreground shadow-lg outline-none transition-[width,height,transform,opacity] duration-200 ease-out motion-reduce:transition-none data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0'>
						<NavigationMenu.Viewport className='relative h-full w-full overflow-hidden' />
					</NavigationMenu.Popup>
				</NavigationMenu.Positioner>
			</NavigationMenu.Portal>
		</NavigationMenu.Root>
	);
};

export { MktMenu };
